import Images from "../utils/Images";

const cardClass =
    "rounded-[14px] px-5 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.06)]";

const bodyClass = "text-[14px] leading-[1.65] text-[#374151]";
const headingClass = "mb-3 text-[15px] font-bold text-[#111827]";

const sections = [
    {
        title: "1. Who this App is for",
        content: (
            <p className={bodyClass}>
                The App is intended for authorized staff of CrewFlow
                to submit food safety and cleaning checklists. It is not
                intended for children under 13 years of age (or the minimum age
                required in your jurisdiction), and we do not knowingly collect
                personal data from children.
            </p>
        ),
    },
    {
        title: "2. Information we collect",
        content: (
            <>
                <p className={`${bodyClass} mb-4`}>
                    Depending on how you use the App, we may collect the
                    following categories of information:
                </p>

                <div className="space-y-4">
                    <div>
                        <h3 className="mb-2 text-[14px] font-bold text-[#111827]">
                            2.1 Account / staff information
                        </h3>
                        <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
                            <li>Registered mobile number (used to sign in)</li>
                            <li>
                                Staff name and outlet assignment (maintained by
                                your employer / admin)
                            </li>
                            <li>Login session details and last login time</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="mb-2 text-[14px] font-bold text-[#111827]">
                            2.2 Checklist submission data
                        </h3>
                        <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
                            <li>
                                Checklist answers (YES/NO), optional and
                                required remarks
                            </li>
                            <li>
                                Checklist category, frequency, and session type
                                (Opening / Closing / General)
                            </li>
                            <li>Business date and submission timestamp</li>
                            <li>Outlet associated with the submission</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="mb-2 text-[14px] font-bold text-[#111827]">
                            2.3 Location information
                        </h3>
                        <ul className={`${bodyClass} mb-3 list-disc space-y-1 pl-5`}>
                            <li>
                                Approximate or precise device location (latitude
                                and longitude)
                            </li>
                            <li>
                                Location accuracy, when provided by the device
                            </li>
                        </ul>
                        <p className={bodyClass}>
                            Location is requested only when you submit a
                            checklist, to verify that the submission was made
                            from the outlet premises / during operational use.
                            Location access can be controlled in your device
                            settings. If location permission is denied, checklist
                            submission may not be possible.
                        </p>
                    </div>

                    <div>
                        <h3 className="mb-2 text-[14px] font-bold text-[#111827]">
                            2.4 Device and technical information
                        </h3>
                        <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
                            <li>
                                Basic technical data such as browser/app type, IP
                                address (if logged by hosting), and session
                                cookies/tokens needed to keep you signed in
                            </li>
                        </ul>
                    </div>
                </div>
            </>
        ),
    },
    {
        title: "3. How we use your information",
        content: (
            <>
                <p className={`${bodyClass} mb-3`}>
                    We use the information for the following business purposes:
                </p>
                <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
                    <li>To authenticate authorized staff and provide App access</li>
                    <li>
                        To record and track food safety / cleaning checklist
                        compliance by outlet
                    </li>
                    <li>
                        To associate submissions with Opening / Closing business
                        dates
                    </li>
                    <li>
                        To verify submission location for operational integrity
                        and audit
                    </li>
                    <li>To improve App reliability, security, and support</li>
                    <li>
                        To comply with legal, regulatory, or audit requirements
                        related to food safety operations
                    </li>
                </ul>
            </>
        ),
    },
    {
        title: "4. Legal basis / permission",
        content: (
            <p className={bodyClass}>
                We process staff information based on employment / operational
                necessity for outlet checklist compliance, and based on your use
                of the App as an authorized staff member. Location data is
                collected with your device permission at the time of checklist
                submission.
            </p>
        ),
    },
    {
        title: "5. How we share information",
        content: (
            <>
                <p className={`${bodyClass} mb-3 font-semibold text-[#111827]`}>
                    We do not sell your personal information.
                </p>
                <p className={`${bodyClass} mb-3`}>
                    We may share information only as needed with:
                </p>
                <ul className={`${bodyClass} list-disc space-y-1 pl-5`}>
                    <li>
                        Authorized managers / administrators of our outlets
                        for operational review and compliance
                    </li>
                    <li>
                        Service providers who host or maintain the App
                        infrastructure (under confidentiality obligations)
                    </li>
                    <li>
                        Authorities or auditors when required by law or food
                        safety regulations
                    </li>
                </ul>
            </>
        ),
    },
    {
        title: "6. Data retention",
        content: (
            <p className={bodyClass}>
                Checklist submissions, related staff identifiers, and location
                coordinates associated with submissions are retained for as long
                as needed for operational, compliance, audit, and legal purposes,
                or as otherwise required by company policy and applicable law.
                Session data is retained only while you remain signed in or until
                the session expires.
            </p>
        ),
    },
    {
        title: "7. Data security",
        content: (
            <p className={bodyClass}>
                We take reasonable technical and organizational measures to
                protect information against unauthorized access, loss, misuse,
                or alteration. However, no method of transmission or storage is
                completely secure, and we cannot guarantee absolute security.
            </p>
        ),
    },
    {
        title: "8. Your choices and rights",
        content: (
            <>
                <p className={`${bodyClass} mb-3`}>
                    Depending on applicable law, you may have rights to:
                </p>
                <ul className={`${bodyClass} mb-4 list-disc space-y-1 pl-5`}>
                    <li>
                        Request access to personal information we hold about you
                    </li>
                    <li>Request correction of inaccurate information</li>
                    <li>
                        Request deletion or restriction where legally permitted
                    </li>
                    <li>
                        Withdraw device location permission (note: this may
                        prevent checklist submission)
                    </li>
                </ul>
                <p className={bodyClass}>
                    Because this App is used for employment / outlet operations,
                    some requests may need to be handled through your outlet
                    manager or company administrator. Contact us using the
                    details below.
                </p>
            </>
        ),
    },
    {
        title: "9. Children's privacy",
        content: (
            <p className={bodyClass}>
                The App is not directed to children. We do not knowingly collect
                personal information from children. If you believe a child has
                provided personal information through the App, please contact us
                so we can take appropriate action.
            </p>
        ),
    },
    {
        title: "10. Third-party services",
        content: (
            <p className={bodyClass}>
                The App may load fonts or interface assets from trusted content
                delivery networks to display the interface. These providers may
                process technical request data (such as IP address) according
                to their own privacy policies. We do not intentionally share
                checklist or staff personal data with advertising networks.
            </p>
        ),
    },
    {
        title: "11. International users",
        content: (
            <p className={bodyClass}>
                If you access the App from outside the country where our servers
                or company operations are located, your information may be
                processed in the country where the App is hosted and where
                our outlets operate.
            </p>
        ),
    },
    {
        title: "12. Changes to this Privacy Policy",
        content: (
            <p className={bodyClass}>
                We may update this Privacy Policy from time to time. The
                &ldquo;Effective date&rdquo; at the top of this page will be
                revised when changes are made. Continued use of the App after an
                update means you acknowledge the revised Privacy Policy.
            </p>
        ),
    },
    {
        title: "13. Contact us",
        contact: true,
        content: (
            <>
                <p className={`${bodyClass} mb-3`}>
                    If you have questions about this Privacy Policy or your
                    information, contact:
                </p>
                <p className="text-[14px] font-semibold text-[#111827]">
                    Team24 Retail Tech Private Limited
                    Developer and publisher of the CrewFlow application
                </p>
                <p className={`${bodyClass} mt-1`}>
                    Email:{" "}
                    <a
                        href="mailto:info@team24.in"
                        className="font-medium text-[#1E9CED] hover:underline"
                    >
                        info@team24.in
                    </a>
                </p>
            </>
        ),
    },
];

export default function PrivacyPolicy() {
    return (
        <main className="min-h-screen bg-[#f3f4f6] px-4 py-8">
            <div className="mx-auto flex w-full max-w-[480px] flex-col gap-3">
                <div className={`${cardClass} bg-[#75BFEE] flex flex-col items-center py-7 text-center`}>
                    <img
                        src={Images.logo}
                        alt="CrewFlow Logo"
                        className="mb-5 w-[180px] max-w-[65vw] object-contain"
                    />

                    <h1 className="text-[22px] font-bold text-[#fff]">
                        Privacy Policy
                    </h1>
                    <p className="mt-1 text-[13px] text-[#fff]">
                        CrewFlow · Effective date: 17 July 2026
                    </p>
                </div>

                <div className={cardClass}>
                    <div className={`space-y-3 ${bodyClass}`}>
                        <p>
                            This Privacy Policy explains how *Team24 Retail Tech Private Limited* ("we", "us", or "our"),
                            the developer and publisher of the *CrewFlow* mobile application, collects, uses, stores,
                            and protects information when authorized staff
                            use the CrewFlow application.
                        </p>
                        <p>
                            CrewFlow is an internal operations application developed and published by Team24 Retail Tech Private Limited for use
                            by authorized staff. The application is used
                            to complete food safety, cleaning, and
                            operational checklists for our outlets.
                        </p>
                        <p>
                            By using the CrewFlow application, you agree to the practices
                            described in this Privacy Policy. If you do not agree,
                            please do not use the application.
                        </p>
                    </div>
                </div>

                {sections.map((section) => (
                    <section
                        key={section.title}
                        className={
                            section.contact
                                ? `${cardClass} border border-[#1E9CED] bg-[#DFEEF5]`
                                : cardClass
                        }
                    >
                        <h2 className={headingClass}>{section.title}</h2>
                        {section.content}
                    </section>
                ))}

                <p className="px-2 py-4 text-center text-[12px] leading-relaxed text-[#6b7280]">
                    This Privacy Policy applies to the CrewFlow application
                    used by authorized staff.
                </p>
            </div>
        </main>
    );
}
